// src/pages/ActivitiesPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Nav from '../components/Nav';
import ActivityCard from '../components/ActivityCard';
import Footer from '../components/Footer';
import { useAuthContext } from '../contexts/AuthContext';
import config from '../config';
import './DashboardPage.css'; // Reusing dashboard styles

export default function ActivitiesPage() {
    const { API_URL } = useAuthContext();
    const [activities, setActivities] = useState([]);
    const [loadingActivities, setLoadingActivities] = useState(true);

    // Upload state
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Set page title
    useEffect(() => {
        document.title = `Activities | ${config.siteTitle}`;
    }, []);

    // Fetch activities on load
    const fetchActivities = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/activities`, {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setActivities(data.activities || []);
            }
        } catch (error) {
            console.error('Failed to fetch activities:', error);
        } finally {
            setLoadingActivities(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchActivities();
    }, [fetchActivities]);

    // Called when header uploads a new activity
    const handleUploadSuccess = () => {
        fetchActivities();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file || uploading) return;

        event.target.value = '';
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}/api/activities/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            if (response.ok) {
                fetchActivities();
            } else {
                console.error('Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="dashboard-page">
            <Nav onUploadSuccess={handleUploadSuccess} />

            <main className="dashboard-content">
                {/* Activity Feed */}
                <section className="activity-feed">
                    <div className="page-header">
                        <h2 className="page-title">Activities</h2>
                        <button
                            className="header-action-button"
                            onClick={handleUploadClick}
                            disabled={uploading}
                        >
                            {uploading ? 'Uploading...' : 'Upload GPX'}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".gpx"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    </div>

                    {loadingActivities ? (
                        <div className="feed-loading">Loading activities...</div>
                    ) : activities.length === 0 ? (
                        <div className="feed-empty">
                            <div className="empty-icon">🏃</div>
                            <h3>No activities yet</h3>
                            <p>Upload a GPX file to see your runs here</p>
                        </div>
                    ) : (
                        <div className="feed-list">
                            {activities.map((activity) => (
                                <ActivityCard key={activity.id} activity={activity} />
                            ))}
                        </div>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}
